import React, { useEffect, useState, useRef } from 'react';
import { FiSave, FiLoader, FiUpload, FiX } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useAuth } from '../../context/AuthContext';

const ACCOUNT_KEYS = [
  ['cash_account_id', 'Cash Account', 'Debited/Credited when invoice payments are received or advances paid via cash.'],
  ['default_bank_account_id', 'Default Bank Account', 'Fallback bank account for all bank transactions (invoice payments, payroll, advances).'],
  ['sales_account_id', 'Sales Account', 'Credited with net sales amount when a Sale Invoice is posted.'],
  ['sales_return_account_id', 'Sales Return Account', 'Debited when a Credit Note (sales return) invoice is posted.'],
  ['commission_account_id', 'Commission Income Account', 'Credited when a Commission Invoice is posted.'],
  ['tax_payable_account_id', 'Tax Payable Account', 'Credited with tax amount on Sale Invoices (if not using Output GST).'],
  ['wht_payable_account_id', 'WHT Payable Account', 'Debited when we withhold tax from a supplier\'s payment (our liability to pay to government).'],
  ['wht_receivable_account_id', 'WHT Receivable Account', 'Debited when a customer withholds tax from our payment (recoverable asset from government).'],
  ['salary_expense_account_id', 'Salary Expense Account', 'Debited with gross salary when Payroll is processed.'],
  ['deductions_payable_account_id', 'Deductions Payable Account', 'Credited with total deductions (PF, advances, etc.) when Payroll is processed.'],
  ['employee_advance_account_id', 'Employee Advance Account', 'Debited when an advance is given to an employee.'],
  ['purchase_expense_account_id', 'Purchase Expense Account', 'Debited with purchase amount (excl. GST) when a Purchase Invoice is posted.'],
  ['purchase_return_account_id', 'Purchase Return Account', 'Credited when a Debit Note (purchase return) is posted.'],
  ['input_gst_account_id', 'Input GST / GST Receivable (Asset)', 'Debited with GST amount on Purchase Invoices. This is a recoverable asset from the government.'],
  ['output_gst_account_id', 'Output GST / GST Payable (Liability)', 'Credited with GST amount on Sale Invoices. This is payable to the government.'],
];

function SectionSaveButton({ onSave, saving }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
        {saving ? <><FiLoader /> Saving...</> : <><FiSave /> Save</>}
      </button>
    </div>
  );
}

const INVOICE_TYPES = [
  { key: 'sale', label: 'Sale Invoice', title: 'SALE INVOICE' },
  { key: 'purchase', label: 'Purchase Invoice', title: 'PURCHASE INVOICE' },
  { key: 'credit', label: 'Credit Note', title: 'CREDIT NOTE' },
  { key: 'debit', label: 'Debit Note', title: 'DEBIT NOTE' },
  { key: 'taxfree', label: 'Unregistered Invoice (UR)', title: 'UNREGISTERED INVOICE' },
  { key: 'commission', label: 'Commission Invoice', title: 'COMMISSION INVOICE' },
];

function InvoiceTypeCard({ type, label, settings, set, logoRefs, handleLogoUpload, saving, onSave }) {
  const logoKey = `${type}_logo_base64`;
  const g = (field) => `${type}_${field}`;
  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-header"><div className="card-title">{label}</div></div>
      <div className="card-body">
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Company Name</label>
            <input className="form-control" value={settings[g('header_name')] || ''} onChange={(e) => set(g('header_name'), e.target.value)} placeholder="e.g. Ahmed Corporation" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-control" value={settings[g('header_phone')] || ''} onChange={(e) => set(g('header_phone'), e.target.value)} placeholder="e.g. 021-12345678" />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Address</label>
            <input className="form-control" value={settings[g('header_address')] || ''} onChange={(e) => set(g('header_address'), e.target.value)} placeholder="Full address for invoice header" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" type="email" value={settings[g('header_email')] || ''} onChange={(e) => set(g('header_email'), e.target.value)} placeholder="e.g. info@ahmedcorporation.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Website</label>
            <input className="form-control" value={settings[g('header_website')] || ''} onChange={(e) => set(g('header_website'), e.target.value)} placeholder="e.g. www.ahmedcorporation.com" />
          </div>
          <div className="form-group">
            <label className="form-label">GST Number</label>
            <input className="form-control" value={settings[g('header_gst')] || ''} onChange={(e) => set(g('header_gst'), e.target.value)} placeholder="e.g. 3277876175600" />
          </div>
          <div className="form-group">
            <label className="form-label">NTN Number</label>
            <input className="form-control" value={settings[g('header_ntn')] || ''} onChange={(e) => set(g('header_ntn'), e.target.value)} placeholder="e.g. 1234567-8" />
          </div>
          <div className="form-group">
            <label className="form-label">Logo <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <input ref={(el) => { logoRefs.current[type] = el; }} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload(e, type)} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" type="button" onClick={() => logoRefs.current[type]?.click()}>
                <FiUpload /> Choose Logo
              </button>
              {settings[logoKey] && (
                <button className="btn btn-danger-outline btn-sm" type="button" onClick={() => set(logoKey, '')}>
                  <FiX /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
        {settings[logoKey] && (
          <div style={{ marginTop: 12, padding: 10, background: '#f9fafb', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={settings[logoKey]} alt="Logo Preview" style={{ maxWidth: 100, maxHeight: 70, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 4, padding: 4, background: '#fff' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Logo Preview</span>
          </div>
        )}
        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Invoice Footer Text <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
          <input className="form-control" value={settings[g('footer_text')] || ''} onChange={(e) => set(g('footer_text'), e.target.value)} placeholder="e.g. Terms & Conditions: Payment due within 30 days." />
        </div>
        <SectionSaveButton onSave={() => onSave(type)} saving={saving === type} />
      </div>
    </div>
  );
}

function GatepassSettingsCard({ settings, set, logoRefs, handleLogoUpload, saving, onSave }) {
  const g = (field) => `gatepass_${field}`;
  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-header"><div className="card-title">Gatepass Print Settings</div></div>
      <div className="card-body">
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Company Name</label>
            <input className="form-control" value={settings[g('header_name')] || ''} onChange={(e) => set(g('header_name'), e.target.value)} placeholder="e.g. Ahmed Corporation" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-control" value={settings[g('header_phone')] || ''} onChange={(e) => set(g('header_phone'), e.target.value)} placeholder="e.g. 021-12345678" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" type="email" value={settings[g('header_email')] || ''} onChange={(e) => set(g('header_email'), e.target.value)} placeholder="e.g. info@ahmedcorporation.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Website</label>
            <input className="form-control" value={settings[g('header_website')] || ''} onChange={(e) => set(g('header_website'), e.target.value)} placeholder="e.g. www.ahmedcorporation.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Logo <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <input ref={(el) => { logoRefs.current.gatepass = el; }} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload(e, 'gatepass')} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" type="button" onClick={() => logoRefs.current.gatepass?.click()}>
                <FiUpload /> Choose Logo
              </button>
              {settings[g('logo_base64')] && (
                <button className="btn btn-danger-outline btn-sm" type="button" onClick={() => set(g('logo_base64'), '')}>
                  <FiX /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
        {settings[g('logo_base64')] && (
          <div style={{ marginTop: 12, padding: 10, background: '#f9fafb', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={settings[g('logo_base64')]} alt="Logo Preview" style={{ maxWidth: 100, maxHeight: 70, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 4, padding: 4, background: '#fff' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Logo Preview</span>
          </div>
        )}
        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Footer Text <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
          <input className="form-control" value={settings[g('footer_text')] || ''} onChange={(e) => set(g('footer_text'), e.target.value)} placeholder="e.g. Terms & Conditions..." />
        </div>
        <SectionSaveButton onSave={() => onSave('gatepass')} saving={saving === 'gatepass'} />
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { hasPermission } = useAuth();
  const [settings, setSettings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [errors, setErrors] = useState({});
  const logoRefs = useRef({});

  const handleLogoUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      showError('Logo too large', 'Image must be under 500 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set(`${type}_logo_base64`, reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  useEffect(() => {
    Promise.all([
      api.settings.get(),
      api.accounts.tree(),
    ]).then(([sRes, aRes]) => {
      const raw = sRes.data.settings || sRes.data || {};
      const flat = {};
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        Object.values(raw).forEach((group) => {
          if (Array.isArray(group)) {
            group.forEach((s) => { flat[s.key] = s.value; });
          }
        });
      }
      setSettings(flat);
      
      // Flatten tree into selectable options with hierarchy labels
      const flatAccounts = [];
      const flattenTree = (nodes, depth = 0) => {
        nodes.forEach((node) => {
          const indent = '  '.repeat(depth);
          const prefix = depth > 0 ? `${indent}${node.name}` : node.name;
          if (!node.children || node.children.length === 0) {
            flatAccounts.push({ value: node.id, label: prefix, depth });
          } else {
            // Add the parent node too (for categories/subtypes that can be selected)
            flatAccounts.push({ value: node.id, label: prefix, depth });
            flattenTree(node.children, depth + 1);
          }
        });
      };
      flattenTree(aRes.data.accounts || []);
      setAccounts(flatAccounts);
      
      setErrors({});
    }).catch((err) => showError('Failed to load settings', extractError(err))).finally(() => setLoading(false));
  }, []);

  const set = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const saveSection = async (sectionKeys, sectionName) => {
    setErrors({});
    setSavingSection(sectionName);
    try {
      await api.settings.update({ settings });
      toast('success', '', `${sectionName} saved successfully`);
    } catch (err) {
      showError(`Failed to save ${sectionName}`, extractError(err));
    } finally {
      setSavingSection(null);
    }
  };

  const saveCompany = () => saveSection(['company_name'], 'Company');
  const saveAccounts = () => {
    const missing = ACCOUNT_KEYS.filter(([key]) => !settings[key]);
    if (missing.length) {
      setErrors({ accounts: 'All account fields are required' });
      return;
    }
    if (!settings.party_account_category_id) {
      setErrors({ accounts: 'Party Account Category is required' });
      return;
    }
    saveSection([...ACCOUNT_KEYS.map(([k]) => k), 'party_account_category_id'], 'Accounting Accounts');
  };
  const saveInvoiceType = (type) => {
    const keys = [
      `${type}_header_name`, `${type}_header_phone`, `${type}_header_address`, `${type}_header_email`,
      `${type}_header_website`, `${type}_header_gst`, `${type}_header_ntn`, `${type}_logo_base64`, `${type}_footer_text`,
    ];
    const label = INVOICE_TYPES.find((t) => t.key === type)?.label || 'Invoice';
    saveSection(keys, `${label} Settings`);
  };
  const saveGatepass = () => {
    const keys = ['gatepass_header_name', 'gatepass_header_phone', 'gatepass_header_email', 'gatepass_header_website', 'gatepass_logo_base64', 'gatepass_footer_text'];
    saveSection(keys, 'Gatepass Settings');
  };
  const saveVoucherSettings = () => saveSection(['voucher_type_lock_enabled'], 'Voucher Settings');
  const savePayroll = () => {
    const errs = {};
    if (!settings.payroll_payment_type) errs.payroll = 'Payment type is required';
    if (!settings.late_deduction_enabled && settings.late_deduction_enabled !== false) errs.payroll = 'Late deduction is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    saveSection(['payroll_payment_type', 'salary_working_days', 'late_deduction_enabled', 'overtime_rate_per_hour'], 'Payroll Policy');
  };

  if (loading) return <Loader text="Loading settings..." />;

  return (
    <div className="page">
      <PageHeader
        title="Settings"
        subtitle="Manage company, accounts, invoices and payroll settings."
        crumbs={[{ label: 'System' }, { label: 'Settings' }]}
      />

      {/* Company */}
      <div className="card">
        <div className="card-header"><div className="card-title">Company</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input className="form-control" value={settings.company_name || ''} onChange={(e) => set('company_name', e.target.value)} placeholder="e.g. Ahmed Corporation" />
            </div>
          </div>
          <SectionSaveButton onSave={saveCompany} saving={savingSection === 'Company'} />
        </div>
      </div>

      {/* Accounting Accounts */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Accounting Accounts</div></div>
        <div className="card-body">
          <div className="grid-3">
            {ACCOUNT_KEYS.map(([key, label, desc]) => (
              <div key={key} className="form-group">
                <label className="form-label">{label} <span className="req">*</span></label>
                <SearchableSelect 
                  options={accounts} 
                  value={settings[key] || ''} 
                  onChange={(v) => set(key, v)} 
                  placeholder="-- Select account --" 
                  isInvalid={!!(errors.accounts && !settings[key])} 
                />
                {desc && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>{desc}</div>}
              </div>
            ))}
            <div className="form-group">
              <label className="form-label">Party Account Parent <span className="req">*</span></label>
              <SearchableSelect
                options={accounts}
                value={settings.party_account_category_id || ''}
                onChange={(v) => set('party_account_category_id', v)}
                placeholder="-- Select parent account --"
                isInvalid={!!(errors.accounts && !settings.party_account_category_id)}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                New party accounts will be auto-created under this parent account.
              </div>
            </div>
          </div>
          {errors.accounts && <div className="invalid-feedback">{errors.accounts}</div>}
          <SectionSaveButton onSave={saveAccounts} saving={savingSection === 'Accounting Accounts'} />
        </div>
      </div>

      {/* Invoice Type Settings */}
      {INVOICE_TYPES.map((t) => (
        <InvoiceTypeCard
          key={t.key}
          type={t.key}
          label={t.label}
          settings={settings}
          set={set}
          logoRefs={logoRefs}
          handleLogoUpload={handleLogoUpload}
          saving={savingSection}
          onSave={saveInvoiceType}
        />
      ))}

      {/* Invoice Number Settings */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Invoice Number Settings</div></div>
        <div className="card-body">
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Set the starting invoice number for each type. New invoices will be generated from this number.</p>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Sale Invoice (SI) Start No.</label>
              <input className="form-control" type="number" value={settings.si_invoice_start || ''} onChange={(e) => set('si_invoice_start', e.target.value)} placeholder="e.g. 750" />
            </div>
            <div className="form-group">
              <label className="form-label">Purchase Invoice (PI) Start No.</label>
              <input className="form-control" type="number" value={settings.purchase_invoice_start || ''} onChange={(e) => set('purchase_invoice_start', e.target.value)} placeholder="e.g. 4544" />
            </div>
            <div className="form-group">
              <label className="form-label">Credit Note (CR) Start No.</label>
              <input className="form-control" type="number" value={settings.cr_invoice_start || ''} onChange={(e) => set('cr_invoice_start', e.target.value)} placeholder="e.g. 99" />
            </div>
            <div className="form-group">
              <label className="form-label">Unregistered (UR) Start No.</label>
              <input className="form-control" type="number" value={settings.ur_invoice_start || ''} onChange={(e) => set('ur_invoice_start', e.target.value)} placeholder="e.g. 1" />
            </div>
            <div className="form-group">
              <label className="form-label">Commission (CM) Start No.</label>
              <input className="form-control" type="number" value={settings.cm_invoice_start || ''} onChange={(e) => set('cm_invoice_start', e.target.value)} placeholder="e.g. 1" />
            </div>
            <div className="form-group">
              <label className="form-label">Gatepass (GP) Start No.</label>
              <input className="form-control" type="number" value={settings.gatepass_start || ''} onChange={(e) => set('gatepass_start', e.target.value)} placeholder="e.g. 1898" />
            </div>
          </div>
          <SectionSaveButton onSave={() => saveSection(['si_invoice_start', 'purchase_invoice_start', 'cr_invoice_start', 'ur_invoice_start', 'cm_invoice_start', 'gatepass_start'], 'Invoice Number Settings')} saving={savingSection === 'Invoice Number Settings'} />
        </div>
      </div>

      {/* Gatepass Settings */}
      <GatepassSettingsCard
        settings={settings}
        set={set}
        logoRefs={logoRefs}
        handleLogoUpload={handleLogoUpload}
        saving={savingSection}
        onSave={saveGatepass}
      />

      {/* Voucher Settings */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Voucher Settings</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Voucher Type Lock</label>
              <SearchableSelect
                options={[{ value: 'true', label: 'Enabled' }, { value: 'false', label: 'Disabled' }]}
                value={settings.voucher_type_lock_enabled == null || !['false', '0', '', 'off'].includes(String(settings.voucher_type_lock_enabled).toLowerCase()) ? 'true' : 'false'}
                onChange={(v) => set('voucher_type_lock_enabled', v)}
                placeholder="-- Select --"
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                Enabled: Bank/Cash Payment & Receipt vouchers lock first row to Cash/Bank account (Debit/Credit forced). Disabled: all voucher types work like General Voucher with open entries.
              </div>
            </div>
          </div>
          <SectionSaveButton onSave={saveVoucherSettings} saving={savingSection === 'Voucher Settings'} />
        </div>
      </div>

      {/* Payroll Policy */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Payroll Policy</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Payroll Payment Type <span className="req">*</span></label>
              <SearchableSelect options={[{ value: 'cash', label: 'Cash' }, { value: 'bank', label: 'Bank' }, { value: 'cheque', label: 'Cheque' }]} value={settings.payroll_payment_type || 'cash'} onChange={(v) => set('payroll_payment_type', v)} placeholder="-- Select --" isInvalid={!!(errors.payroll && !settings.payroll_payment_type)} />
            </div>
            <div className="form-group">
              <label className="form-label">Salary Working Days</label>
              <input className="form-control" type="number" value={settings.salary_working_days || 26} onChange={(e) => set('salary_working_days', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Late Deduction <span className="req">*</span></label>
              <SearchableSelect options={[{ value: 'true', label: 'Enabled' }, { value: 'false', label: 'Disabled' }]} value={String(settings.late_deduction_enabled)} onChange={(v) => set('late_deduction_enabled', v === 'true')} placeholder="-- Select --" isInvalid={!!(errors.payroll && !settings.late_deduction_enabled && settings.late_deduction_enabled !== false)} />
            </div>
            <div className="form-group">
              <label className="form-label">Overtime Rate (Rs./hr)</label>
              <input className="form-control" type="number" value={settings.overtime_rate_per_hour || 800} onChange={(e) => set('overtime_rate_per_hour', e.target.value)} />
            </div>
          </div>
          {errors.payroll && <div className="invalid-feedback">{errors.payroll}</div>}
          <SectionSaveButton onSave={savePayroll} saving={savingSection === 'Payroll Policy'} />
        </div>
      </div>
    </div>
  );
}
